import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import i18n from "../../i18n";
import {
  buildBeginCheckoutEvent,
  buildUserContext,
  pushToDataLayer,
  selectCatalogueState,
} from "../../utils/analytics/gtm";

const useGtmCheckoutTracker = () => {
  const order = useSelector((state) => state?.yodaOrders?.order);
  const orderRowDetails = useSelector(
    (state) => state?.yodaOrders?.orderRowDetails,
  );
  const stakeholder = useSelector((state) => state?.yodaStakeholders?.me);
  const customer = useSelector((state) => state?.yodaCustomer?.customer);
  const catalogueState = useSelector(selectCatalogueState);

  const trackedOrderIdsRef = useRef(new Set());

  const rows = useMemo(
    () =>
      Object.values(orderRowDetails || {}).filter(
        (row) => (row?.quantity || row?.currentQuantity) > 0,
      ),
    [orderRowDetails],
  );

  // Quando l'ordine viene azzerato (RESET_ORDER imposta order=null),
  // puliamo il Set così l'evento verrà ri-emesso al prossimo caricamento
  // completo (prodotto + cataloghi).
  useEffect(() => {
    if (!order?.id) {
      trackedOrderIdsRef.current.clear();
    }
  }, [order?.id]);

  useEffect(() => {
    if (!order?.id || !rows.length) {
      return;
    }

    const orderId = String(order.id);
    if (trackedOrderIdsRef.current.has(orderId)) {
      return;
    }

    const persistedCustomerId =
      typeof window !== "undefined"
        ? window.localStorage.getItem("selectedCustomer")
        : undefined;
    const persistedAgentId =
      typeof window !== "undefined"
        ? window.localStorage.getItem("selectedAgent")
        : undefined;

    const userContext = buildUserContext({
      stakeholder,
      customer,
      persistedCustomerId,
      persistedAgentId,
    });

    const payload = buildBeginCheckoutEvent({
      order,
      rows,
      catalogueState,
      customer,
      userContext,
      language: i18n?.resolvedLanguage || i18n?.language,
    });

    console.log("[GTM DEBUG begin_checkout] rows:", rows);
    console.log(
      "[GTM DEBUG begin_checkout] catalogueItemDetails keys:",
      Object.keys(catalogueState?.catalogueItemDetails || {}),
    );
    console.log(
      "[GTM DEBUG begin_checkout] rows[0].productId:",
      rows?.[0]?.productId,
    );
    console.log("[GTM DEBUG begin_checkout] payload:", payload);

    if (!payload) {
      console.warn(
        "[GTM DEBUG begin_checkout] payload vuoto - items non mappati. Catalogo caricato?",
        Object.keys(catalogueState?.catalogueItemDetails || {}).length > 0,
      );
      return;
    }

    // Aspetta che tutti i prodotti abbiano il loro catalogo caricato in Redux.
    // orderReset chiama clearOrderCatalog() che svuota catalogueItemDetails;
    // i cataloghi si ricaricano uno alla volta, quindi l'effetto può sparare
    // con catalogueState parziale. Verifichiamo che ogni row.productId abbia
    // un'entry in catalogueItemDetails prima di emettere.
    const allProducts = {
      ...(catalogueState?.catalogueItemDetails || {}),
      ...(catalogueState?.catalogueItemDetailsSwap || {}),
    };
    const catalogueModelCodes = new Set(
      Object.values(allProducts)
        .map((p) => p?.modelCode)
        .filter(Boolean),
    );

    const allCataloguesReady = rows.every((row) => {
      // row.productId non è mai presente nel Redux store (il BE non lo restituisce).
      // Usiamo modelCode estratto da row.fullCode (sempre presente, formato:
      // seasonCode_collectionCode_modelCode_materialCode_colorCode).
      const pid = row.productId;
      if (pid) {
        return !!(
          catalogueState?.catalogueItemDetails?.[pid] ||
          catalogueState?.catalogueItemDetailsSwap?.[pid]
        );
      }
      const fullCodeParts = row.fullCode ? row.fullCode.split("_") : [];
      const mc = fullCodeParts.length >= 3 ? fullCodeParts[2] : null;
      if (mc) return catalogueModelCodes.has(mc);
      return true; // non possiamo determinarlo, lasciamo passare
    });

    const allItemsMapped =
      allCataloguesReady &&
      payload.ecommerce.items.length === rows.length &&
      payload.ecommerce.items.every((item) => item.item_id);

    if (!allItemsMapped) {
      return;
    }

    pushToDataLayer(payload);
    trackedOrderIdsRef.current.add(orderId);
  }, [order, rows, stakeholder, customer, catalogueState]);
};

export default useGtmCheckoutTracker;
